import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';

function Point3({ projectId }) {
  const [activeTab, setActiveTab] = useState('internal');
  const [internalResult, setInternalResult] = useState(() => {
    const saved = localStorage.getItem(`analysis_internal_${projectId}`);
    return saved ? JSON.parse(saved) : null;
  });
  const [externalResult, setExternalResult] = useState(() => {
    const saved = localStorage.getItem(`analysis_external_${projectId}`);
    return saved ? JSON.parse(saved) : null;
  });
  const [dafoResult, setDafoResult] = useState(() => {
    const saved = localStorage.getItem(`analysis_dafo_${projectId}`);
    return saved ? JSON.parse(saved) : null;
  });
  const [loading, setLoading] = useState(false);

  const internalTemplates = {
    'Análisis RRHH': `Analiza los recursos humanos de la empresa:
- Estructura organizativa
- Número de empleados por departamento
- Perfiles profesionales clave
- Capacidades y competencias del equipo
- Política de formación y desarrollo
- Rotación y retención de talento`,
    
    'Recursos y Capacidades': `Evalúa los recursos y capacidades de la empresa:
- Recursos financieros disponibles
- Infraestructura y instalaciones
- Tecnología y sistemas
- Capacidad productiva
- Recursos intangibles (marca, patentes, know-how)
- Certificaciones y acreditaciones`,
    
    'Cadena de Valor': `Analiza la cadena de valor de la empresa:
- Actividades primarias (producción, marketing, ventas)
- Actividades de apoyo (compras, I+D, RRHH)
- Proveedores principales
- Canales de distribución
- Relaciones con clientes
- Márgenes y rentabilidad por actividad`,
    
    'Personalizado Interno': ''
  };

  const externalTemplates = {
    'Análisis del Sector': `Analiza el sector de actividad de la empresa:
- Tamaño y crecimiento del mercado
- Principales competidores
- Barreras de entrada y salida
- Poder de negociación de clientes y proveedores
- Productos sustitutos
- Regulación y normativas del sector`,
    
    'Competencia Directa': `Analiza la competencia directa:
- Identificación de competidores principales
- Cuota de mercado de cada competidor
- Estrategias competitivas
- Fortalezas y debilidades de competidores
- Posicionamiento en el mercado
- Políticas de precios`,
    
    'Entorno Macro': `Evalúa el entorno macroeconómico:
- Factores políticos y legales
- Tendencias económicas
- Factores sociales y demográficos
- Avances tecnológicos
- Factores medioambientales
- Oportunidades y amenazas del entorno`,
    
    'Personalizado Externo': ''
  };

  const handleInternalAnalysis = async (template, customPrompt = '') => {
    const prompt = template === 'Personalizado Interno' ? customPrompt : internalTemplates[template];
    
    setLoading(true);
    try {
      const response = await axios.post('/api/analysis/', {
        project_id: projectId,
        point_number: 31,
        prompt,
        use_web: false,
        search_terms: '',
        web_only: false
      });
      
      const result = { ...response.data, template, generatedAt: new Date().toISOString() };
      setInternalResult(result);
      localStorage.setItem(`analysis_internal_${projectId}`, JSON.stringify(result));
      localStorage.setItem(`analysis_point3_${projectId}`, JSON.stringify(result));
    } catch (error) {
      console.error('Error in internal analysis:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExternalAnalysis = async (template, searchTerms = '', customPrompt = '') => {
    const prompt = template === 'Personalizado Externo' ? customPrompt : externalTemplates[template];
    
    setLoading(true);
    try {
      const response = await axios.post('/api/analysis/', {
        project_id: projectId,
        point_number: 32,
        prompt,
        use_web: true,
        search_terms: searchTerms,
        web_only: false
      });
      
      const result = { ...response.data, template, searchTerms, generatedAt: new Date().toISOString() };
      setExternalResult(result);
      localStorage.setItem(`analysis_external_${projectId}`, JSON.stringify(result));
      localStorage.setItem(`analysis_point3_${projectId}`, JSON.stringify(result));
    } catch (error) {
      console.error('Error in external analysis:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateDAFO = async (isAutomatic = false) => {
    setLoading(true);
    try {
      let combinedContext = '';
      
      if (internalResult && externalResult) {
        combinedContext = `
ANÁLISIS INTERNO PREVIO:
${internalResult.content}

ANÁLISIS EXTERNO PREVIO:
${externalResult.content}`;
      }

      const dafoPrompt = `Basándote en la información disponible, genera una matriz DAFO completa con:

FORTALEZAS (Factores internos positivos):
- Enumera y explica las principales fortalezas

DEBILIDADES (Factores internos negativos):
- Enumera y explica las principales debilidades

OPORTUNIDADES (Factores externos positivos):
- Enumera y explica las principales oportunidades

AMENAZAS (Factores externos negativos):
- Enumera y explica las principales amenazas

ESTRATEGIAS DAFO:
- Estrategias FO (Fortalezas-Oportunidades)
- Estrategias FA (Fortalezas-Amenazas)
- Estrategias DO (Debilidades-Oportunidades)
- Estrategias DA (Debilidades-Amenazas)

${combinedContext}`;

      const response = await axios.post('/api/analysis/', {
        project_id: projectId,
        point_number: 33,
        prompt: dafoPrompt,
        use_web: !isAutomatic,
        search_terms: isAutomatic ? '' : 'análisis DAFO sector empresa',
        web_only: false
      });
      
      const result = { 
        ...response.data, 
        isAutomatic,
        basedOn: { internal: !!internalResult, external: !!externalResult },
        generatedAt: new Date().toISOString() 
      };
      setDafoResult(result);
      localStorage.setItem(`analysis_dafo_${projectId}`, JSON.stringify(result));
      localStorage.setItem(`analysis_point3_${projectId}`, JSON.stringify(result));
    } catch (error) {
      console.error('Error generating DAFO:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="point-content">
      <h2>3. Análisis de Situación</h2>
      
      <div className="tabs">
        <div className="tab-buttons">
          <button 
            className={`tab-btn ${activeTab === 'internal' ? 'active' : ''}`}
            onClick={() => setActiveTab('internal')}
          >
            Análisis Interno
          </button>
          <button 
            className={`tab-btn ${activeTab === 'external' ? 'active' : ''}`}
            onClick={() => setActiveTab('external')}
          >
            Análisis Externo
          </button>
          <button 
            className={`tab-btn ${activeTab === 'dafo' ? 'active' : ''}`}
            onClick={() => setActiveTab('dafo')}
          >
            Matriz DAFO
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'internal' && (
            <InternalAnalysisTab
              templates={internalTemplates}
              onAnalysis={handleInternalAnalysis}
              result={internalResult}
              loading={loading}
            />
          )}

          {activeTab === 'external' && (
            <ExternalAnalysisTab
              templates={externalTemplates}
              onAnalysis={handleExternalAnalysis}
              result={externalResult}
              loading={loading}
            />
          )}

          {activeTab === 'dafo' && (
            <DAFOTab
              internalResult={internalResult}
              externalResult={externalResult}
              dafoResult={dafoResult}
              onGenerateDAFO={generateDAFO}
              loading={loading}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function InternalAnalysisTab({ templates, onAnalysis, result, loading }) {
  const [selectedTemplate, setSelectedTemplate] = useState('Análisis RRHH');
  const [customPrompt, setCustomPrompt] = useState('');

  const handleAnalysis = () => {
    if (selectedTemplate === 'Personalizado Interno') {
      if (!customPrompt.trim()) return;
      onAnalysis(selectedTemplate, customPrompt);
    } else {
      onAnalysis(selectedTemplate);
    }
  };

  return (
    <div className="analysis-tab">
      <div className="two-cols">
        <div className="col-left">
          <h3>Plantillas disponibles:</h3>
          
          <div className="template-selector">
            <select 
              value={selectedTemplate}
              onChange={(e) => setSelectedTemplate(e.target.value)}
            >
              {Object.keys(templates).map(template => (
                <option key={template} value={template}>{template}</option>
              ))}
            </select>
          </div>

          <button 
            onClick={handleAnalysis}
            disabled={loading || (selectedTemplate === 'Personalizado Interno' && !customPrompt.trim())}
            className="analyze-btn"
          >
            {loading ? 'Analizando...' : 'Ejecutar Análisis Interno'}
          </button>
        </div>

        <div className="col-right">
          <h3>Prompt para análisis interno:</h3>
          
          {selectedTemplate === 'Personalizado Interno' ? (
            <textarea
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              placeholder="Escribe tu prompt personalizado..."
              rows={8}
            />
          ) : (
            <textarea
              value={templates[selectedTemplate]}
              readOnly
              rows={8}
            />
          )}

          {result && (
            <div className="analysis-result">
              <h4>Resultado Análisis Interno</h4>
              <div className="result-meta">
                <span>Plantilla: {result.template}</span>
                <span>Generado: {new Date(result.generatedAt).toLocaleString()}</span>
              </div>
              <div className="result-content">
                <ReactMarkdown>{result.content}</ReactMarkdown>
              </div>
              {result.sources && result.sources.length > 0 && (
                <div className="sources">
                  <h5>Fuentes utilizadas:</h5>
                  <ul>
                    {result.sources.map((source, index) => (
                      <li key={index}>{source}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ExternalAnalysisTab({ templates, onAnalysis, result, loading }) {
  const [selectedTemplate, setSelectedTemplate] = useState('Análisis del Sector');
  const [searchTerms, setSearchTerms] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');

  const handleAnalysis = () => {
    if (selectedTemplate === 'Personalizado Externo') {
      if (!customPrompt.trim()) return;
      onAnalysis(selectedTemplate, searchTerms, customPrompt);
    } else {
      onAnalysis(selectedTemplate, searchTerms);
    }
  };

  return (
    <div className="analysis-tab">
      <div className="two-cols">
        <div className="col-left">
          <h3>Plantillas disponibles:</h3>
          
          <div className="template-selector">
            <select 
              value={selectedTemplate}
              onChange={(e) => setSelectedTemplate(e.target.value)}
            >
              {Object.keys(templates).map(template => (
                <option key={template} value={template}>{template}</option>
              ))}
            </select>
          </div>

          <div className="search-terms">
            <label>Términos búsqueda adicionales:</label>
            <input
              type="text"
              value={searchTerms}
              onChange={(e) => setSearchTerms(e.target.value)}
              placeholder="competidores, sector, mercado..."
            />
          </div>

          <button 
            onClick={handleAnalysis}
            disabled={loading || (selectedTemplate === 'Personalizado Externo' && !customPrompt.trim())}
            className="analyze-btn"
          >
            {loading ? 'Analizando...' : 'Ejecutar Análisis Externo'}
          </button>
        </div>

        <div className="col-right">
          <h3>Prompt para análisis externo:</h3>
          
          {selectedTemplate === 'Personalizado Externo' ? (
            <textarea
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              placeholder="Escribe tu prompt personalizado..."
              rows={8}
            />
          ) : (
            <textarea
              value={templates[selectedTemplate]}
              readOnly
              rows={8}
            />
          )}

          {result && (
            <div className="analysis-result">
              <h4>Resultado Análisis Externo</h4>
              <div className="result-meta">
                <span>Plantilla: {result.template}</span>
                {result.searchTerms && <span>Búsqueda: {result.searchTerms}</span>}
                <span>Generado: {new Date(result.generatedAt).toLocaleString()}</span>
              </div>
              <div className="result-content">
                <ReactMarkdown>{result.content}</ReactMarkdown>
              </div>
              {result.sources && result.sources.length > 0 && (
                <div className="sources">
                  <h5>Fuentes utilizadas:</h5>
                  <ul>
                    {result.sources.map((source, index) => (
                      <li key={index}>{source}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DAFOTab({ internalResult, externalResult, dafoResult, onGenerateDAFO, loading }) {
  const hasInternal = !!internalResult;
  const hasExternal = !!externalResult;
  const hasBoth = hasInternal && hasExternal;

  return (
    <div className="analysis-tab">
      <div className="two-cols">
        <div className="col-left">
          <h3>Generación automática de DAFO</h3>
          
          <div className="dafo-status">
            <div className={`status-item ${hasInternal ? 'completed' : 'pending'}`}>
              <span>Análisis Interno</span>
              <span className="status">{hasInternal ? '✓' : '○'}</span>
            </div>
            <div className={`status-item ${hasExternal ? 'completed' : 'pending'}`}>
              <span>Análisis Externo</span>
              <span className="status">{hasExternal ? '✓' : '○'}</span>
            </div>
          </div>

          {hasBoth ? (
            <div className="dafo-actions">
              <div className="info-box success">
                Análisis interno y externo completados
              </div>
              <button 
                onClick={() => onGenerateDAFO(true)}
                disabled={loading}
                className="analyze-btn"
              >
                {loading ? 'Generando...' : 'Generar DAFO Automático'}
              </button>
            </div>
          ) : (
            <div className="dafo-actions">
              <div className="info-box warning">
                Completa primero el análisis {!hasInternal && !hasExternal ? 'interno y externo' : !hasInternal ? 'interno' : 'externo'}
              </div>
            </div>
          )}

          <div className="manual-dafo">
            <h4>Opción alternativa:</h4>
            <button 
              onClick={() => onGenerateDAFO(false)}
              disabled={loading}
              className="analyze-btn secondary"
            >
              {loading ? 'Generando...' : 'DAFO Manual'}
            </button>
            <p className="help-text">Genera DAFO con información disponible + búsqueda web</p>
          </div>
        </div>

        <div className="col-right">
          {dafoResult ? (
            <div className="analysis-result">
              <h4>Matriz DAFO Generada</h4>
              <div className="result-meta">
                <span>Tipo: {dafoResult.isAutomatic ? 'Automático' : 'Manual'}</span>
                {dafoResult.basedOn && (
                  <span>Basado en: {dafoResult.basedOn.internal ? 'Interno' : ''} {dafoResult.basedOn.external ? 'Externo' : ''}</span>
                )}
                <span>Generado: {new Date(dafoResult.generatedAt).toLocaleString()}</span>
              </div>
              <div className="result-content">
                <ReactMarkdown>{dafoResult.content}</ReactMarkdown>
              </div>
              {dafoResult.sources && dafoResult.sources.length > 0 && (
                <div className="sources">
                  <h5>Fuentes utilizadas:</h5>
                  <ul>
                    {dafoResult.sources.map((source, index) => (
                      <li key={index}>{source}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="export-actions">
                <button 
                  onClick={() => {
                    const element = document.createElement('a');
                    const file = new Blob([dafoResult.content], {type: 'text/plain'});
                    element.href = URL.createObjectURL(file);
                    element.download = `DAFO_${Date.now()}.txt`;
                    document.body.appendChild(element);
                    element.click();
                    document.body.removeChild(element);
                  }}
                  className="export-btn"
                >
                  Exportar DAFO
                </button>
              </div>
            </div>
          ) : (
            <div className="info-box">
              Ejecuta la generación de DAFO para ver los resultados aquí
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Point3;